import { Box, Skeleton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { useFormFactor } from '../../../hooks'
import PromotedContentCardContent from './card-content'
import PromotedCardContainer from './container'
import usePromotedCardMethods, { PROMOTED_CARD_TYPES } from './use-promoted-card-methods'

const multiNoWrap = lines => {
  return {
    lineClamp: lines,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical'
  }
}

// MT.3: desktop layout is a clean flex row — image takes 45% on the left,
// content takes the remaining 55% with its own padding. No more absolute-
// positioned content panel overlapping the image. Mobile keeps the
// stacked image-above-content layout via the existing absolute styles.
const mobileImgContainer = {
  width: '100%',
  height: '50%',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0
}

const imgContainer = {
  flex: '0 0 45%',
  height: '100%'
}

const mobileContentContainer = {
  width: 'calc(100% - 32px)',
  background: '#FFF',
  justifyContent: 'center',
  position: 'absolute',
  top: '35%',
  bottom: 0,
  right: '16px',
  left: '16px'
}

const contentContainer = {
  flex: 1,
  // Override the calc()-based width that lives in the shared
  // PromotedContentCardContent styles — flex sizing handles the panel now.
  width: 'auto',
  background: '#FFF',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
}

const useStyles = makeStyles(({ spacing, palette, typography }) => ({
  root: {
    height: '100%'
  },
  container: props => props.isMobile
    ? {
        position: 'relative',
        width: '100%',
        height: '379px'
      }
    : {
        width: '100%',
        minHeight: '379px',
        alignItems: 'stretch'
      },
  imgContainer: props => props.isMobile ? { ...mobileImgContainer } : { ...imgContainer },
  contentContainer: props => props.isMobile ? { ...mobileContentContainer } : { ...contentContainer },
  image: {
    height: '100%',
    width: '100%',
    objectFit: 'cover'
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: '28px',
    lineHeight: '32px',
    letterSpacing: '0.6px',
    color: '#000',
    ...multiNoWrap(2)
  },
  cardSummary: {
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '24px',
    color: '#454545',
    ...multiNoWrap(4)
  },
  time: {
    color: palette.primary.main,
    fontWeight: typography.fontWeightBold
  },
  actions: {
    marginTop: spacing(1)
  }
}))

const PromotedContentMainCard = ({ content, onSelect, position, testId, size, onAddToPlaylist, ...props }) => {
  const { isMobile } = useFormFactor()
  const classes = useStyles({ isMobile })

  const {
    contentImage,
    handleCardSelect,
    expanded,
    cardSize,
    hoverShadow,
    actions
  } = usePromotedCardMethods({ content, position, size, cardType: PROMOTED_CARD_TYPES.MAIN, onSelect, onAddToPlaylist })

  const handleImageKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCardSelect()
    }
  }

  return (
    <PromotedCardContainer
      expand={expanded}
      size={cardSize}
      hoverShadow={hoverShadow}
      content={content}
      testId={testId}
    >
      <Box display='flex' flexDirection='row' className={classes.container}>
        <Box
          className={classes.imgContainer}
          onClick={handleCardSelect}
          onKeyDown={handleImageKeyDown}
          role='button'
          tabIndex={0}
          aria-label={content ? `View ${content.title}` : 'Loading content'}
          sx={{
            cursor: 'pointer',
            '&:focus': {
              outline: '2px solid #1976d2',
              outlineOffset: '2px'
            }
          }}
        >
          {
              content
                ? (
                  <img
                    src={contentImage}
                    title={content?.image?.altText}
                    className={classes.image}
                    data-test={`card-image-${content?.friendlyUrl}`}
                    alt={content?.image?.altText || content?.title}
                  />
                  )
                : (
                  <Skeleton variant='rectangular' className={classes.image} />
                  )
            }
        </Box>
        <PromotedContentCardContent
          content={content}
          cardType={PROMOTED_CARD_TYPES.MAIN}
          actions={actions}
          onSelect={handleCardSelect}
          summaryLines={2}
          expand={expanded}
          subtitleMargin={1}
          overrides={{
            contentContainer: classes.contentContainer,
            actions: classes.actions
          }}
        />
      </Box>
    </PromotedCardContainer>
  )
}

export default PromotedContentMainCard
